import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  ChevronRightIcon,
  ChevronDownIcon,
  FolderIcon,
  DocumentIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';
import { PanelLoading } from '@/components/ui/loading';
import { API_BASE_URL } from '@/config';

interface WBSNode {
  id: string;
  code: string;
  name: string;
  level: number;
  children: WBSNode[];
  activities: number;
  progress: number;
  status: 'On Track' | 'At Risk' | 'Delayed';
  baselineProgress?: number;
  variance?: number;
}

// Custom hook for WBS data
const useWBSData = (projectId: string | undefined) => {
  const [wbsData, setWbsData] = useState<WBSNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    const fetchWBSData = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`${API_BASE_URL}/activities/projects/${projectId}/wbs/all`, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(res.statusText || 'Failed to load WBS');
        const data = await res.json();
        const rows = Array.isArray(data) ? data : [];
        const byId = new Map<string, { wbs_id: string; wbs_name: string; wbs_short_name?: string; parent_wbs_id?: string | null }>();
        rows.forEach((r: { wbs_id?: number; wbs_name?: string; wbs_short_name?: string; parent_wbs_id?: number | null }) => {
          const id = String(r.wbs_id ?? '');
          byId.set(id, {
            wbs_id: id,
            wbs_name: String(r.wbs_name ?? ''),
            wbs_short_name: r.wbs_short_name != null ? String(r.wbs_short_name) : undefined,
            parent_wbs_id: r.parent_wbs_id != null ? String(r.parent_wbs_id) : null,
          });
        });
        const childrenByParent = new Map<string, Array<{ wbs_id: string }>>();
        byId.forEach((row, id) => {
          const pid = row.parent_wbs_id ?? '';
          if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
          childrenByParent.get(pid)!.push({ wbs_id: row.wbs_id });
        });
        const toNode = (id: string, level: number): WBSNode => {
          const row = byId.get(id);
          const childIds = childrenByParent.get(id) ?? [];
          const children = childIds.map((c) => toNode(c.wbs_id, level + 1));
          return {
            id,
            code: row?.wbs_short_name ?? id,
            name: row?.wbs_name ?? id,
            level,
            children,
            activities: 0,
            progress: 0,
            status: 'On Track',
          };
        };
        const allIds = Array.from(byId.keys());
        const rootIds = allIds.filter((id) => {
          const row = byId.get(id);
          const pid = row?.parent_wbs_id;
          return pid == null || pid === '' || !byId.has(pid);
        });
        const tree = rootIds.length > 0
          ? rootIds.map((id) => toNode(id, 0))
          : allIds.map((id) => toNode(id, 0));
        setWbsData(tree);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load WBS data');
      } finally {
        setLoading(false);
      }
    };

    fetchWBSData();
  }, [projectId]);

  return { wbsData, loading, error };
};

const ProjectWBS: React.FC = () => {
  const { projectId } = useParams();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [showBaseline, setShowBaseline] = useState(true);
  const [filterStatus, setFilterStatus] = useState<'all' | 'On Track' | 'At Risk' | 'Delayed'>('all');

  const { wbsData, loading, error } = useWBSData(projectId);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'On Track':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'At Risk':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Delayed':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getVarianceColor = (variance: number): string => {
    if (variance > 0) return 'text-green-600';
    if (variance < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const toggleNode = (nodeId: string): void => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderWBSNode = (node: WBSNode, depth: number = 0): JSX.Element => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;
    const isSelected = selectedNode === node.id;

    return (
      <div key={node.id}>
        <div
          className={`flex items-center p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
            isSelected ? 'bg-blue-50 border-blue-200' : ''
          }`}
          style={{ paddingLeft: `${depth * 24 + 16}px` }}
          onClick={() => setSelectedNode(node.id)}
        >
          {/* Expand/Collapse Icon */}
          <div className="flex items-center justify-center w-6 h-6 mr-2">
            {hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNode(node.id);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="w-4 h-4" />
            )}
          </div>

          {/* Node Icon */}
          <div className="flex items-center justify-center w-6 h-6 mr-3">
            {hasChildren ? (
              <FolderIcon className="h-5 w-5 text-blue-500" />
            ) : (
              <DocumentIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>

          {/* Node Code and Name */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500 min-w-0">
                {node.code}
              </span>
              <span className="text-sm font-medium text-gray-900 truncate">
                {node.name}
              </span>
            </div>
          </div>

          {/* Activities Count */}
          <div className="flex items-center mr-4">
            <UserGroupIcon className="h-4 w-4 text-gray-400 mr-1" />
            <span className="text-sm text-gray-600">{node.activities}</span>
          </div>

          {/* Progress */}
          <div className="flex items-center mr-4 min-w-0">
            <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${node.progress}%` }}
              ></div>
            </div>
            <span className="text-sm text-gray-900 min-w-0">{node.progress}%</span>
          </div>

          {/* Baseline Comparison */}
          {showBaseline && node.baselineProgress !== undefined && (
            <div className="flex items-center mr-4 min-w-0">
              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                <div 
                  className="bg-purple-400 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${node.baselineProgress}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-900 min-w-0">{node.baselineProgress}%</span>
            </div>
          )}

          {/* Variance */}
          {showBaseline && node.variance !== undefined && (
            <div className="flex items-center mr-4">
              <span className={`text-sm font-medium ${getVarianceColor(node.variance)}`}>
                {node.variance > 0 ? '+' : ''}{node.variance}%
              </span>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(node.status)}`}>
              {node.status}
            </span>
          </div>
        </div>

        {/* Render children if expanded */}
        {isExpanded && hasChildren && (
          <div>
            {node.children.map(child => renderWBSNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const filteredWBSData = wbsData.filter(node => {
    if (filterStatus === 'all') return true;
    return node.status === filterStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <PanelLoading message="Loading WBS data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading WBS</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Work Breakdown Structure</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Hierarchical view of project work packages and activities
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {/* Baseline Toggle */}
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showBaseline}
                    onChange={(e) => setShowBaseline(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show Baseline</span>
                </label>

                {/* Status Filter */}
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                  className="block w-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="On Track">On Track</option>
                  <option value="At Risk">At Risk</option>
                  <option value="Delayed">Delayed</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Legend */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Legend</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-blue-600 rounded mr-2"></div>
              <span className="text-sm text-gray-600">Current Progress</span>
            </div>
            {showBaseline && (
              <div className="flex items-center">
                <div className="w-4 h-4 bg-purple-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">Baseline Progress</span>
              </div>
            )}
            <div className="flex items-center">
              <FolderIcon className="h-4 w-4 text-blue-500 mr-2" />
              <span className="text-sm text-gray-600">Work Package</span>
            </div>
            <div className="flex items-center">
              <DocumentIcon className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">Activity</span>
            </div>
          </div>
        </div>

        {/* WBS Tree */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="flex-1 flex items-center space-x-4">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  WBS Structure
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activities
                </span>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </span>
                {showBaseline && (
                  <>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Baseline
                    </span>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Variance
                    </span>
                  </>
                )}
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </span>
              </div>
            </div>
          </div>

          {/* WBS Content */}
          <div className="divide-y divide-gray-200">
            {filteredWBSData.length > 0 ? (
              filteredWBSData.map(node => renderWBSNode(node))
            ) : (
              <div className="text-center py-12">
                <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-sm font-medium text-gray-900 mb-2">No WBS data found</h3>
                <p className="text-sm text-gray-500">
                  {wbsData.length === 0 ? 'No WBS data available for this project.' : 'Try adjusting your filter criteria.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Selected Node Details */}
        {selectedNode && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Node Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Progress Overview</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Current Progress</span>
                    <span className="font-medium text-gray-900">--</span>
                  </div>
                  {showBaseline && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Baseline Progress</span>
                      <span className="font-medium text-gray-900">--</span>
                    </div>
                  )}
                  {showBaseline && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Variance</span>
                      <span className="font-medium text-gray-900">--</span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Activities</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Activities</span>
                    <span className="font-medium text-gray-900">--</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Completed</span>
                    <span className="font-medium text-green-600">--</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">In Progress</span>
                    <span className="font-medium text-blue-600">--</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Not Started</span>
                    <span className="font-medium text-gray-600">--</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Timeline</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Start Date</span>
                    <span className="font-medium text-gray-900">--</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">End Date</span>
                    <span className="font-medium text-gray-900">--</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Duration</span>
                    <span className="font-medium text-gray-900">--</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectWBS; 

